import { DateTime } from 'luxon';
import { DEFAULT_LANGUAGE } from "../config/constants";

/**
 * Converts a JS Date or ISO string to a Luxon date
 * @param {String | Date} date 
 * @returns 
 */
const getLuxonDate = (date = new Date()) => {
  return DateTime.fromJSDate(new Date(date))
};

/**
 * Get the start of the current today
 * @returns
 */
export const getStartOfToday = () => {
  return getLuxonDate().startOf('day');
};

/**
 * Format a day to a specific format like 'MM-dd-yyyy'
 * @param {String | Date} date 
 * @param {String} format 
 * @returns
 */
export const formatDate = (date = new Date(), format = 'dd-MM-yyyy', locale = DEFAULT_LANGUAGE) => {
  return getLuxonDate(date).setLocale(locale).toFormat(format);
};

/**
 * Check if two dates are of the same type, like day, month, or year
 * @param {String | Date} firstDate 
 * @param {String | Date} secondDate 
 * @param {String} diffType 
 * @returns 
 */
export const isSame = (firstDate = new Date(), secondDate = new Date(), diffType = 'day') => {
  let pluralDiffType = 'days';
  switch(diffType) {
    case 'minute':
      pluralDiffType = 'minutes';
      break;
    case 'second':
      pluralDiffType = 'seconds';
      break;
    case 'hour':
      pluralDiffType = 'hours';
      break;
    case 'week':
      pluralDiffType = 'weeks';
      break;
    case 'month':
      pluralDiffType = 'months';
      break;
    case 'year':
      pluralDiffType = 'years';
      break;
    case 'day':
    default:
      break;
  }

  const diffBetweenDates = getDiffBetweenDates(firstDate, secondDate, pluralDiffType);
  return diffBetweenDates >= 0 && diffBetweenDates <= 1;
};

/**
 * Get the difference between two dates as a number in days, hours, weeks etc
 * @param {String | Date} firstDate 
 * @param {String | Date} secondDate 
 * @param {String} diffType 
 * @returns 
 */
export const getDiffBetweenDates = (firstDate = new Date(), secondDate = new Date(), diffType = 'days') => {
  const firstDateLuxon = getLuxonDate(firstDate).startOf('day');
  const secondDateLuxon = getLuxonDate(secondDate).startOf('day');
  const diff = firstDateLuxon.diff(secondDateLuxon, diffType);

  if (diff[diffType]) {
    return diff[diffType];
  }

  return 0;
};

/**
 * Add a number of type to a date, for instance, add 2 'years' to a date
 * @param {String | Date} date 
 * @param {Number} amount 
 * @param {String} type 
 * @returns 
 */
export const addToDate = (date = new Date(), amount = 0, type = 'days') => {
  const luxonDate = getLuxonDate(date);

  const params = {};
  params[type] = amount;
  return luxonDate.plus(params);
};

/**
 * Subtract a number of type from a date, for instance, add 2 'years' to a date
 * @param {String | Date} date 
 * @param {Number} amount 
 * @param {String} type 
 * @returns 
 */
export const subtractFromDate = (date = new Date(), amount = 0, type = 'days') => {
  const luxonDate = getLuxonDate(date);

  const minusParam = {};
  minusParam[type] = amount;
  return luxonDate.minus(minusParam);
};

/**
 * Get a relative date based on a relativitystring like -5years or -10days
 * @param {String | Date} date 
 * @param {Number} amount 
 * @param {String} type 
 * @returns 
 */
export const getRelativeDate = (date = new Date(), relativityString = '-5year') => {
  const luxonDate = getLuxonDate(date);
  if (relativityString === 'now') {
    return formatDate(luxonDate, 'yyyy');
  }

  const numbers = relativityString.match(/[-0-9.]/g);
  const strings = relativityString.match(/[a-zA-Z]+/g);

  if (!numbers || !strings) {
    return luxonDate;
  }
  
  if (numbers.length === 0 || strings.length !== 1) {
    return luxonDate;
  }

  const amount = Number(numbers.join(''));
  const value = strings[0];

  if (amount > 0) {
    return addToDate(luxonDate, amount, value);
  } else {
    return subtractFromDate(luxonDate, Math.abs(amount), value);
  }
};

export default {
  addToDate,
  formatDate,
  getDiffBetweenDates,
  getRelativeDate,
  getStartOfToday,
  isSame,
  subtractFromDate,
}
